import * as z from "zod";

export const feeTimingSchema = z.enum(['upfront', 'at_closing']).default('upfront');

export const apportionmentSchema = z.object({
  apportionment_party_id: z
    .union([z.number(), z.string()])
    .transform((val) => (typeof val === "string" ? parseInt(val) : val)),
  amount: z.number().min(0),
});

export const feeSchema = z.object({
  fee_id: z.number(),
  name: z.string().min(1, "Fee name is required"),
  multiply_by_applicants: z.boolean().default(false),
  married_count_as_one: z.boolean().default(false),
  amount: z.number().min(0),
  timing: feeTimingSchema,
  apportionment: z.array(apportionmentSchema).default([]),
});

export const deliveryFeeSchema = z.object({
  fee_id: z.number(),
  name: z.string().min(1, "Fee name is required"),
  multiply_by_applicants: z.boolean().default(false),
  married_count_as_one: z.boolean().default(false),
  amount: z.number().min(0),
  timing: feeTimingSchema,
  apportionment: z.array(apportionmentSchema).default([])
});

export const documentFeeSchema = z.object({
  document_type_id: z.number(),
  document_type_name: z.string().optional(),
  fees: z.array(feeSchema).default([]),
});

export const documentDeliveryFeeSchema = z.object({
  document_type_id: z.number(),
  document_type_name: z.string().optional(),
  delivery_time_id: z.number(),
  delivery_time_name: z.string().optional(),
  delivery_time_days: z.number().optional(),
  fees: z.array(deliveryFeeSchema).default([]),
});

export const documentCustomFormFeeSchema = z.object({
  document_type_id: z.number(),
  document_type_name: z.string().optional(),
  accept_custom_form: z.boolean().optional(),
  amount: z.number().min(0),
  timing: feeTimingSchema,
  apportionment: z.array(apportionmentSchema).default([])
});

export const transactionFeeSchema = feeSchema;

export const bundleDocumentSchema = z.object({
  document_type_id: z.number(),
  document_type_name: z.string(),
});

export const bundleSchema = z.object({
  bundle_id: z.number().optional(),
  name: z.string().min(1, "Bundle name is required"),
  multiply_by_applicants: z.boolean().default(false),
  married_count_as_one: z.boolean().default(false),
  amount: z.number().min(0),
  timing: feeTimingSchema,
  documents: z.array(bundleDocumentSchema).default([]),
  apportionment: z.array(apportionmentSchema).default([]),
});

export const pricingTableFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  transaction_fees: z.array(transactionFeeSchema).default([]),
  document_fees: z.array(documentFeeSchema).default([]),
  bundles: z.array(bundleSchema).default([]),
  delivery_fees: z.array(documentDeliveryFeeSchema).default([]),
  custom_form_fees: z.array(documentCustomFormFeeSchema).default([]),
});

export type PricingTableFormValues = z.infer<typeof pricingTableFormSchema>;
export type TransactionFeeValues = z.infer<typeof transactionFeeSchema>;
export type DocumentFeeValues = z.infer<typeof documentFeeSchema>;
export type DocumentDeliveryFeeValues = z.infer<
  typeof documentDeliveryFeeSchema
>;
export type DocumentCustomFormFeeValues = z.infer<
  typeof documentCustomFormFeeSchema
>;
export type FeeValues = z.infer<typeof feeSchema>;
export type DeliveryFeeValues = z.infer<typeof deliveryFeeSchema>;

export type ApportionmentValues = z.infer<typeof apportionmentSchema>;
export type BundleValues = z.infer<typeof bundleSchema>;
export type BundleDocumentValues = z.infer<typeof bundleDocumentSchema>;
